// Cross-tab authentication synchronization utility
// Prevents token interference between different system logins in multiple tabs
import { getAuthCookieValue, removeAuthCookieValue, setAuthCookieValue } from "./authCookie";

class AuthSync {
  constructor() {
    this.currentToken = getAuthCookieValue("authToken");
    this.isInitialized = false;
    this.refreshTimeout = null;

    // Check BroadcastChannel support
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.bc = new BroadcastChannel('auth-sync');
      } catch (error) {
        console.warn('AuthSync: BroadcastChannel initialization failed, using storage events only', error);
        this.bc = null;
      }
    } else {
      console.warn('AuthSync: BroadcastChannel not supported, using storage events only');
      this.bc = null;
    }

    this.init();
  }

  init() {
    try {
      // Listen for messages from other tabs (if BroadcastChannel is available)
      if (this.bc) {
        this.bc.onmessage = (event) => {
          this.handleCrossTabMessage(event);
        };
      }

      // Fallback: Listen for storage events (works across tabs)
      window.addEventListener('storage', (event) => {
        this.handleStorageEvent(event);
      });

      // Mark as initialized after a short delay to avoid initial refresh
      setTimeout(() => {
        this.isInitialized = true;
      }, 1000);
    } catch (error) {
      console.error('AuthSync: Initialization failed', error);
      // Still mark as initialized to prevent blocking
      this.isInitialized = true;
    }
  }

  handleCrossTabMessage(event) {
    try {
      const { type, token } = event.data || {};

      if (!this.isInitialized) {
        return; // Ignore messages during initialization
      }

      if (type === 'token_changed' && token && token !== this.currentToken) {
        console.log('AuthSync: Token changed in another tab - refreshing page');
        this.refreshPage();
      }
      // Note: We don't refresh on logout (when token is null/falsy)
    } catch (error) {
      console.error('AuthSync: Error handling cross-tab message', error);
    }
  }

  handleStorageEvent(event) {
    try {
      if (!this.isInitialized) {
        return; // Ignore events during initialization
      }

      if (event.key === 'authToken') {
        const newToken = event.newValue;
        const oldToken = event.oldValue;

        // Only refresh if:
        // 1. New token exists (not logout)
        // 2. New token is different from current token
        // 3. This is a real change (not just initialization)
        if (newToken && newToken !== this.currentToken && oldToken !== null) {
          console.log('AuthSync: Token changed in another tab via storage event - refreshing page');
          this.refreshPage();
        }
      }
    } catch (error) {
      console.error('AuthSync: Error handling storage event', error);
    }
  }

  setAuthToken(token, keepSignedIn = true) {
    try {
      const previousToken = this.currentToken;
      this.currentToken = token;

      if (token) {
        setAuthCookieValue("authToken", token, keepSignedIn);
      } else {
        removeAuthCookieValue("authToken");
      }

      // Notify other tabs only if this is a login (token is truthy)
      // and it's different from the previous token
      if (token && token !== previousToken && this.isInitialized && this.bc) {
        this.bc.postMessage({
          type: 'token_changed',
          token: token,
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.error('AuthSync: Error setting auth token', error);
      // Still try cookie/localStorage helper as fallback
      try {
        if (token) {
          setAuthCookieValue("authToken", token, keepSignedIn);
        } else {
          removeAuthCookieValue("authToken");
        }
        this.currentToken = token;
      } catch (storageError) {
        console.error('AuthSync: Critical error - cannot access localStorage', storageError);
      }
    }
  }

  clearAuthToken() {
    this.currentToken = null;
    removeAuthCookieValue("authToken");

    // We don't notify other tabs on logout to prevent unnecessary refreshes
    // The storage event will handle this naturally
  }

  getAuthToken() {
    return getAuthCookieValue("authToken");
  }

  refreshPage() {
    // Prevent multiple simultaneous refreshes
    if (this.refreshTimeout) {
      return;
    }

    // Add a small delay to ensure the message is processed
    this.refreshTimeout = setTimeout(() => {
      try {
        window.location.reload();
      } catch (error) {
        console.error('AuthSync: Error refreshing page', error);
      }
    }, 100);
  }

  destroy() {
    try {
      if (this.bc) {
        this.bc.close();
      }
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
      }
      window.removeEventListener('storage', this.handleStorageEvent);
    } catch (error) {
      console.error('AuthSync: Error during cleanup', error);
    }
  }
}

// Create a singleton instance
const authSync = new AuthSync();

// Export the methods
export const setAuthToken = (token, keepSignedIn) => authSync.setAuthToken(token, keepSignedIn);
export const clearAuthToken = () => authSync.clearAuthToken();
export const getAuthToken = () => authSync.getAuthToken();
export const updateAuthToken = (token, keepSignedIn) => authSync.setAuthToken(token, keepSignedIn); // Alias for compatibility

// Export the instance for advanced usage
export default authSync;
